import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FileRejection } from 'react-dropzone';
import { TriangleAlertIcon } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import {
  useImportDeliveryFile,
  type CampaignToCreate,
  type DeliveryFileProblem,
  type DeliveryImportOutcome,
  type PlatformNotice,
} from '@/entities/media-campaign';
import { connectorPlatformLabel } from '@/entities/media-connector';
import { track } from '@/shared/analytics';
import { FileDropzone } from '@/shared/ui/FileDropzone';
import { formatShortDate } from '@/shared/utils/format';

const MAX_SIZE = 10 * 1024 * 1024;

const ACCEPTED_FILE_TYPES: Record<string, string[]> = {
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

type ImportFailure = Extract<DeliveryImportOutcome, { kind: 'failed' }>;

type Step =
  | { kind: 'file'; invalid: boolean }
  | {
      kind: 'review';
      file: File;
      problems: DeliveryFileProblem[];
      campaigns: CampaignToCreate[];
    }
  | { kind: 'imported'; outcome: Extract<DeliveryImportOutcome, { kind: 'imported' }> }
  | {
      kind: 'failed';
      file: File;
      createCampaigns?: string[];
      outcome: ImportFailure;
    };

export interface DeliveryFileUploadDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
}

export function DeliveryFileUploadDialog({
  projectId,
  open,
  onClose,
}: DeliveryFileUploadDialogProps) {
  const { t, i18n } = useTranslation('projects');
  const importFile = useImportDeliveryFile(projectId);
  const [step, setStep] = useState<Step>({ kind: 'file', invalid: false });
  const busy = importFile.isPending;

  const send = async (file: File, createCampaigns?: string[]) => {
    const outcome = await importFile.mutateAsync({ file, createCampaigns });
    if (outcome.kind === 'imported') {
      track('performance_delivery_imported', {
        project_id: projectId,
        rows_written: outcome.rowsWritten,
        created_campaigns: outcome.createdCampaigns.length,
      });
      setStep({ kind: 'imported', outcome });
    } else if (outcome.kind === 'refused') {
      track('performance_delivery_import_refused', {
        project_id: projectId,
        problem_count: outcome.problems.length,
        campaigns_to_create: outcome.campaignsToCreate.length,
      });
      setStep({
        kind: 'review',
        file,
        problems: outcome.problems,
        campaigns: outcome.campaignsToCreate,
      });
    } else {
      setStep({ kind: 'failed', file, createCampaigns, outcome });
    }
  };

  const handleDrop = (accepted: File[], rejections: FileRejection[]) => {
    if (accepted.length !== 1 || rejections.length > 0) {
      setStep({ kind: 'file', invalid: true });
      return;
    }
    void send(accepted[0]);
  };

  const confirm = () => {
    if (step.kind === 'review')
      void send(
        step.file,
        step.campaigns.map(({ name }) => name),
      );
  };

  const chooseAnotherFile = () => setStep({ kind: 'file', invalid: false });

  const failureMessage = (outcome: ImportFailure) => {
    if (outcome.code === 'campaign_name_taken') {
      return t('projectDetail.manageCampaigns.upload.nameTaken');
    }
    if (outcome.code === 'delivery_import_in_progress') {
      return t('projectDetail.manageCampaigns.upload.inProgress');
    }
    return outcome.detail || t('projectDetail.manageCampaigns.upload.failed');
  };

  const platformNoticeText = (notice: PlatformNotice) => {
    const values = { campaign: notice.campaignName, labels: notice.labels.join(', ') };
    if (notice.code === 'unknown_platform') {
      return t('projectDetail.manageCampaigns.upload.platformUnknown', values);
    }
    if (notice.code === 'mixed_platforms') {
      return t('projectDetail.manageCampaigns.upload.platformMixed', values);
    }
    if (notice.code === 'platform_kept' && notice.keptPlatform) {
      return t('projectDetail.manageCampaigns.upload.platformKept', {
        ...values,
        platform: connectorPlatformLabel(notice.keptPlatform),
      });
    }
    return null;
  };
  const platformNotices =
    step.kind === 'imported'
      ? step.outcome.platformNotices.flatMap((notice) => platformNoticeText(notice) ?? [])
      : [];

  const reviewHasProblems = step.kind === 'review' && step.problems.length > 0;

  const campaignList = (campaigns: CampaignToCreate[]) => (
    <ul className="flex flex-col gap-2xs text-sm">
      {campaigns.map((campaign) => (
        <li key={campaign.name} className="flex justify-between gap-sm">
          <span className="truncate">{campaign.name}</span>
          <span className="shrink-0 text-muted-foreground">
            {t('projectDetail.manageCampaigns.upload.rows', { count: campaign.rows })}
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent scrollable className="max-w-180">
        <DialogHeader>
          <DialogTitle>
            {step.kind === 'imported'
              ? t('projectDetail.manageCampaigns.upload.resultTitle')
              : t('projectDetail.manageCampaigns.upload.title')}
          </DialogTitle>
          <DialogDescription>
            {t('projectDetail.manageCampaigns.upload.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-md pt-md">
          {step.kind === 'file' && (
            <>
              <FileDropzone
                accept={ACCEPTED_FILE_TYPES}
                maxSize={MAX_SIZE}
                multiple={false}
                disabled={busy}
                title={
                  busy
                    ? t('projectDetail.manageCampaigns.upload.reading')
                    : t('projectDetail.manageCampaigns.upload.dropTitle')
                }
                caption={t('projectDetail.manageCampaigns.upload.dropCaption')}
                onDrop={handleDrop}
              />
              {step.invalid && (
                <Alert variant="destructive" icon={<TriangleAlertIcon />}>
                  <AlertDescription>
                    {t('projectDetail.manageCampaigns.upload.invalidFile')}
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {step.kind === 'review' && reviewHasProblems && (
            <>
              <Alert variant="destructive" icon={<TriangleAlertIcon />}>
                <AlertTitle>
                  {t('projectDetail.manageCampaigns.upload.problemsTitle', {
                    count: step.problems.length,
                  })}
                </AlertTitle>
                <AlertDescription>
                  {t('projectDetail.manageCampaigns.upload.problemsHint')}
                </AlertDescription>
              </Alert>
              <ul className="flex flex-col gap-xs text-sm">
                {step.problems.map((problem, index) => (
                  <li key={index} className="flex gap-sm">
                    <span className="shrink-0 font-medium">
                      {problem.row === null
                        ? t('projectDetail.manageCampaigns.upload.wholeFile')
                        : t('projectDetail.manageCampaigns.upload.rowNumber', { row: problem.row })}
                      {problem.column ? ` · ${problem.column}` : ''}
                    </span>
                    <span className="text-muted-foreground">{problem.message}</span>
                  </li>
                ))}
              </ul>
              {step.campaigns.length > 0 && (
                <div className="flex flex-col gap-xs">
                  <p className="font-medium">
                    {t('projectDetail.manageCampaigns.upload.notInProjectTitle', {
                      count: step.campaigns.length,
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('projectDetail.manageCampaigns.upload.notInProjectHint')}
                  </p>
                  {campaignList(step.campaigns)}
                </div>
              )}
            </>
          )}

          {step.kind === 'review' && !reviewHasProblems && (
            <div className="flex flex-col gap-xs">
              <p className="font-medium">
                {t('projectDetail.manageCampaigns.upload.newCampaignsTitle', {
                  count: step.campaigns.length,
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('projectDetail.manageCampaigns.upload.newCampaignsHint')}
              </p>
              {campaignList(step.campaigns)}
            </div>
          )}

          {step.kind === 'imported' && (
            <div className="flex flex-col gap-sm text-sm">
              <p>
                {t('projectDetail.manageCampaigns.upload.rowsWritten', {
                  count: step.outcome.rowsWritten,
                })}
              </p>
              <p>
                {t('projectDetail.manageCampaigns.upload.datesCovered', {
                  start: formatShortDate(step.outcome.windowStart, i18n.language),
                  end: formatShortDate(step.outcome.windowEnd, i18n.language),
                })}
              </p>
              {step.outcome.createdCampaigns.length > 0 && (
                <p>
                  {t('projectDetail.manageCampaigns.upload.createdCampaigns', {
                    count: step.outcome.createdCampaigns.length,
                    names: step.outcome.createdCampaigns.map(({ name }) => name).join(', '),
                  })}
                </p>
              )}
              {step.outcome.ignoredColumns.length > 0 && (
                <p className="text-muted-foreground">
                  {t('projectDetail.manageCampaigns.upload.ignoredColumns', {
                    columns: step.outcome.ignoredColumns.join(', '),
                  })}
                </p>
              )}
              {platformNotices.length > 0 && (
                <Alert icon={<TriangleAlertIcon />}>
                  <AlertTitle>
                    {t('projectDetail.manageCampaigns.upload.platformNoticesTitle')}
                  </AlertTitle>
                  <AlertDescription>
                    <ul className="flex flex-col gap-2xs">
                      {platformNotices.map((notice) => (
                        <li key={notice}>{notice}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
              {step.outcome.clearedColumns.length > 0 && (
                <Alert icon={<TriangleAlertIcon />}>
                  <AlertTitle>{t('projectDetail.manageCampaigns.upload.clearedTitle')}</AlertTitle>
                  <AlertDescription>
                    {t('projectDetail.manageCampaigns.upload.cleared', {
                      metrics: step.outcome.clearedColumns.join(', '),
                    })}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step.kind === 'failed' && (
            <Alert variant="destructive" icon={<TriangleAlertIcon />}>
              <AlertDescription>{failureMessage(step.outcome)}</AlertDescription>
            </Alert>
          )}
        </DialogBody>

        <DialogFooter>
          {step.kind === 'imported' ? (
            <Button onClick={onClose}>{t('projectDetail.manageCampaigns.upload.done')}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={busy}>
                {t('projectDetail.manageCampaigns.upload.cancel')}
              </Button>
              {(step.kind === 'review' || step.kind === 'failed') && (
                <Button variant="outline" onClick={chooseAnotherFile} disabled={busy}>
                  {t('projectDetail.manageCampaigns.upload.chooseAnotherFile')}
                </Button>
              )}
              {step.kind === 'failed' && (
                <Button onClick={() => void send(step.file, step.createCampaigns)} disabled={busy}>
                  {t('projectDetail.manageCampaigns.upload.sendAgain')}
                </Button>
              )}
              {step.kind === 'review' && !reviewHasProblems && (
                <Button onClick={confirm} disabled={busy}>
                  {busy
                    ? t('projectDetail.manageCampaigns.upload.importing')
                    : t('projectDetail.manageCampaigns.upload.import')}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
